import { useEffect, useState } from "react";
import { Plus, KeyRound, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { fmtDate } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";

const EMPTY = { username: "", password: "", name: "", email: "", role: "user" };

export const UsersPanel = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [resetUser, setResetUser] = useState(null);
  const [newPw, setNewPw] = useState("");
  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/users", form); toast.success("User created"); setCreateOpen(false); setForm(EMPTY); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const reset = async (e) => {
    e.preventDefault();
    try { await api.post(`/users/${resetUser.id}/reset-password`, { new_password: newPw }); toast.success(`Password reset for ${resetUser.username}`); setResetUser(null); setNewPw(""); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const changeRole = async (u, role) => {
    try { await api.put(`/users/${u.id}`, { name: u.name, email: u.email, role }); toast.success("Role updated"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Delete user ${u.username}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("User deleted"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div data-testid="users-panel">
      <div className="mb-4 flex justify-end"><Button data-testid="create-user-button" onClick={() => setCreateOpen(true)} className="bg-red-600 hover:bg-red-700"><Plus className="mr-1 h-4 w-4" />Create user</Button></div>
      <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-zinc-200 bg-slate-50 text-left font-mono text-[11px] uppercase tracking-wider text-zinc-500">
            <tr><th className="px-5 py-3">Username</th><th className="px-3">Name</th><th className="px-3">Email</th><th className="px-3">Role</th><th className="px-3">Created</th><th className="w-48" /></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.username}`} className="border-b border-zinc-100 last:border-0">
                <td className="px-5 py-3 font-mono font-semibold">{u.username}</td>
                <td className="px-3">{u.name}</td>
                <td className="px-3 text-zinc-500">{u.email}</td>
                <td className="px-3">
                  <select data-testid={`user-role-${u.username}`} disabled={u.id === me.id} value={u.role} onChange={(e) => changeRole(u, e.target.value)} className="h-8 rounded-md border border-zinc-200 bg-white px-2 text-sm">
                    <option value="user">User</option><option value="admin">Admin</option>
                  </select>
                </td>
                <td className="px-3 text-zinc-500">{fmtDate(u.created_at)}</td>
                <td className="px-3 text-right">
                  <Button data-testid={`reset-password-${u.username}`} variant="ghost" size="sm" onClick={() => setResetUser(u)}><KeyRound className="mr-1 h-4 w-4" />Reset password</Button>
                  {u.id !== me.id && <button data-testid={`delete-user-${u.username}`} onClick={() => remove(u)} className="rounded p-2 text-zinc-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-user-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Create user</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div><Label>Username</Label><Input data-testid="new-user-username" required minLength={3} value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} /></div>
            <div><Label>Temporary password (min 6)</Label><Input data-testid="new-user-password" required minLength={6} type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            <div><Label>Full name</Label><Input data-testid="new-user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Email</Label><Input data-testid="new-user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Role</Label><select data-testid="new-user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm"><option value="user">User</option><option value="admin">Admin</option></select></div>
            <DialogFooter><Button data-testid="new-user-submit" type="submit" className="bg-red-600 hover:bg-red-700">Create user</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetUser} onOpenChange={(o) => !o && setResetUser(null)}>
        <DialogContent data-testid="reset-password-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Reset password · {resetUser?.username}</DialogTitle></DialogHeader>
          <form onSubmit={reset} className="space-y-3">
            <div><Label>New password (min 6)</Label><Input data-testid="reset-password-input" required minLength={6} type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} /></div>
            <DialogFooter><Button data-testid="reset-password-submit" type="submit" className="bg-red-600 hover:bg-red-700">Reset password</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
